<script setup>
import { onMounted, ref, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import Button from 'dashboard/components-next/button/Button.vue';
import SubAccountsList from './SubAccountsList.vue';
import TemplatesPanel from './TemplatesPanel.vue';
import OverviewCards from './OverviewCards.vue';
import CreateSubAccountDialog from './CreateSubAccountDialog.vue';
import BrandingForm from './BrandingForm.vue';
import ProviderForm from './ProviderForm.vue';
import PlansPanel from './PlansPanel.vue';
import WholesalePanel from './WholesalePanel.vue';
import CommissionsPanel from './CommissionsPanel.vue';
import AmbassadorPagePanel from './AmbassadorPagePanel.vue';

const store = useStore();
const { t } = useI18n();
const { currentAccount } = useAccount();

const subAccounts = useMapGetter('reseller/getSubAccounts');
const uiFlags = useMapGetter('reseller/getUIFlags');
const profile = useMapGetter('reseller/getProfile');

const isWhiteLabel = computed(
  () => currentAccount.value?.partner_type === 'white_label'
);

// Link de indicação (revenda por comissão): quem se cadastrar por ele fica
// atribuído à revenda e gera comissão nos 3 níveis.
const referralLink = computed(() => profile.value?.referral_link || '');
// Embaixador ou revenda ativos, com a página de vendas configurada, recebem `ambassador`.
const ambassador = computed(() => profile.value?.ambassador || null);

const tp = (key, fallback) => {
  const value = t(key);
  return value === key ? fallback : value;
};

const copyReferral = async () => {
  if (!referralLink.value) return;
  try {
    await copyTextToClipboard(referralLink.value);
    useAlert(tp('REVENDA.REFERRAL.COPIED', 'Link copiado!'));
  } catch (error) {
    useAlert(tp('REVENDA.REFERRAL.COPY_ERROR', 'Não foi possível copiar.'));
  }
};

const showCreate = ref(false);
const activeTab = ref('clients');

const isLoading = computed(() => uiFlags.value.isFetching);

const tabs = computed(() =>
  [
    {
      key: 'clients',
      label: t('REVENDA.TABS.CLIENTS'),
      icon: 'i-lucide-users',
    },
    {
      key: 'templates',
      label: t('REVENDA.TABS.TEMPLATES'),
      icon: 'i-lucide-layout-template',
    },
    isWhiteLabel.value
      ? {
          key: 'branding',
          label: t('REVENDA.TABS.BRANDING'),
          icon: 'i-lucide-palette',
        }
      : null,
    // Planos só para MARCA BRANCA (cria planos próprios). Revenda por comissão
    // vende NOSSOS planos e ganha comissão — não gerencia planos.
    isWhiteLabel.value
      ? {
          key: 'plans',
          label: t('REVENDA.TABS.PLANS'),
          icon: 'i-lucide-tags',
        }
      : null,
    // Pagamentos (chave própria p/ receber direto) só para MARCA BRANCA.
    // Revenda por comissão NÃO recebe direto — nós recebemos e creditamos.
    isWhiteLabel.value
      ? {
          key: 'provider',
          label: t('REVENDA.TABS.PROVIDER'),
          icon: 'i-lucide-wallet',
        }
      : null,
    // Atacado (preço de custo) só faz sentido para MARCA BRANCA. Revenda por
    // comissão não define preço — vende NOSSOS planos e ganha comissão.
    isWhiteLabel.value
      ? {
          key: 'wholesale',
          label: t('REVENDA.TABS.WHOLESALE'),
          icon: 'i-lucide-receipt',
        }
      : null,
    {
      key: 'commissions',
      label: t('REVENDA.TABS.COMMISSIONS'),
      icon: 'i-lucide-coins',
    },
  ].filter(Boolean)
);

const openCreate = () => {
  showCreate.value = true;
};

const closeCreate = () => {
  showCreate.value = false;
};

const onCreated = () => {
  store.dispatch('reseller/get');
};

onMounted(() => {
  store.dispatch('reseller/get');
  store.dispatch('reseller/getProfile');
});
</script>

<template>
  <div
    class="flex flex-col w-full h-full gap-5 p-4 overflow-auto sm:p-6 bg-n-background"
  >
    <header
      class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
    >
      <div class="flex flex-col gap-1">
        <h1 class="text-xl font-medium text-n-slate-12">
          {{ t('REVENDA.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11">
          {{ t('REVENDA.SUBTITLE') }}
        </p>
      </div>
      <Button
        v-if="activeTab === 'clients'"
        icon="i-lucide-circle-plus"
        class="self-start shrink-0"
        :label="t('REVENDA.SUB_ACCOUNTS.NEW')"
        @click="openCreate"
      />
    </header>

    <OverviewCards />

    <!-- Link de indicação (revenda por comissão) -->
    <div
      v-if="!isWhiteLabel && referralLink"
      class="flex flex-col gap-2 p-4 border rounded-xl border-n-weak bg-n-solid-2"
    >
      <div class="flex items-center gap-2">
        <span class="i-lucide-link size-4 text-n-brand" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ tp('REVENDA.REFERRAL.TITLE', 'Seu link de indicação') }}
        </span>
      </div>
      <p class="text-xs text-n-slate-11">
        {{
          tp(
            'REVENDA.REFERRAL.HINT',
            'Compartilhe este link. Quem se cadastrar por ele fica ligado a você e gera comissão nos 3 níveis.'
          )
        }}
      </p>
      <div class="flex items-center gap-2">
        <input
          :value="referralLink"
          readonly
          class="flex-1 px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12"
          @focus="$event.target.select()"
        />
        <Button
          icon="i-lucide-copy"
          :label="tp('REVENDA.REFERRAL.COPY', 'Copiar')"
          @click="copyReferral"
        />
      </div>
    </div>

    <AmbassadorPagePanel v-if="ambassador" :ambassador="ambassador" />

    <!-- Tabs (scroll horizontal no mobile, sem barra vertical) -->
    <nav
      class="flex gap-1 border-b border-n-weak overflow-x-auto overflow-y-hidden scrollbar-hide shrink-0"
    >
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        class="flex items-center gap-2 px-3 py-2 -mb-px text-sm border-b-2 transition-colors shrink-0 whitespace-nowrap"
        :class="
          activeTab === tab.key
            ? 'border-n-brand text-n-slate-12 font-medium'
            : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
        "
        @click="activeTab = tab.key"
      >
        <span :class="tab.icon" class="text-base" />
        {{ tab.label }}
      </button>
    </nav>

    <!-- Tab content -->
    <section class="flex-1">
      <SubAccountsList
        v-if="activeTab === 'clients'"
        :sub-accounts="subAccounts"
        :is-loading="isLoading"
      />
      <TemplatesPanel v-else-if="activeTab === 'templates'" />
      <BrandingForm v-else-if="activeTab === 'branding' && isWhiteLabel" />
      <PlansPanel v-else-if="activeTab === 'plans' && isWhiteLabel" />
      <ProviderForm v-else-if="activeTab === 'provider' && isWhiteLabel" />
      <WholesalePanel v-else-if="activeTab === 'wholesale' && isWhiteLabel" />
      <CommissionsPanel v-else-if="activeTab === 'commissions'" />
    </section>

    <CreateSubAccountDialog
      v-if="showCreate"
      @close="closeCreate"
      @created="onCreated"
    />
  </div>
</template>
